import { logger } from "@/utils/logger";
import { useCallback, useMemo } from 'react';
import { useDealerSession } from '@/contexts/DealerSessionContext';
import { useFormContext } from '@/contexts/FormContext';
import { useToast } from '@/hooks/use-toast';

interface OptimizedFormSubmissionHook {
  handleFormSubmit: (submissionData: any) => Promise<void>;
  processFormEmails: () => Promise<{ [key: string]: string }>;
}

export const useOptimizedFormSubmission = (): OptimizedFormSubmissionHook => {
  const { sessionData } = useDealerSession();
  const { activeForm } = useFormContext();
  const { toast } = useToast();

  // PII storage functionality removed

  // Memoized form email processor
  const processFormEmails = useCallback(async (): Promise<{ [key: string]: string }> => {
    logger.log('🔑 OPTIMIZED_FORM: Processing form emails');

    try {
      // Check for department-specific emails in the DOM
      const serviceEmailElement = document.querySelector('input[name="serviceemails"]') as HTMLInputElement;
      const financeEmailElement = document.querySelector('input[name="financeemails"]') as HTMLInputElement;
      const usedEmailElement = document.querySelector('input[name="usedemails"]') as HTMLInputElement;
      const generalEmailElement = document.querySelector('input[name="generalemails"]') as HTMLInputElement;

      const emails = {
        service: serviceEmailElement?.value || generalEmailElement?.value || 'service@dealer.com',
        finance: financeEmailElement?.value || generalEmailElement?.value || 'finance@dealer.com',
        used: usedEmailElement?.value || generalEmailElement?.value || 'sales@dealer.com',
        general: generalEmailElement?.value || 'info@dealer.com'
      };

      logger.log('✅ OPTIMIZED_FORM: Form emails processed:', emails);
      
      // Trigger debug modal if enabled and session data is available
      if (window.localStorage.getItem('debug_form_emails') === 'true' && sessionData) {
        const debugEvent = new CustomEvent('showEmailsDebug', { 
          detail: { 
            formEmails: Object.entries(emails).map(([dept, email]) => ({
              Department: dept,
              ADF_email: email,
              HTML_email: email
            })),
            dealerId: sessionData.dealerId,
            domain: window.location.hostname
          } 
        });
        window.dispatchEvent(debugEvent);
      }
      
      return emails;
    } catch (error) {
      console.error('❌ OPTIMIZED_FORM: Error processing form emails:', error);
      return {
        service: 'service@dealer.com',
        finance: 'finance@dealer.com', 
        used: 'sales@dealer.com',
        general: 'info@dealer.com'
      };
    }
  }, [sessionData, activeForm]);

  // Main form submission handler
  const handleFormSubmit = useCallback(async (submissionData: any): Promise<void> => {
    logger.log('🚀 OPTIMIZED_FORM: Starting form submission process');

    try {
      // Process form emails first
      const formEmails = await processFormEmails();

      // PII storage removed

      // Show success message
      toast({
        title: 'Form Submitted',
        description: 'Your form has been submitted successfully.',
      });

      // Set form submission timestamp to prevent same form reopening
      localStorage.setItem('lastFormSubmissionTime', Date.now().toString());

      logger.log('✅ OPTIMIZED_FORM: Form submission completed');
    } catch (error) {
      console.error('❌ OPTIMIZED_FORM: Form submission failed:', error);
      
      toast({
        title: 'Error',
        description: 'An error occurred while submitting the form.',
        variant: 'destructive'
      });
      
      throw error;
    }
  }, [sessionData, processFormEmails, toast]);

  // Memoize the return object to prevent unnecessary re-renders
  return useMemo(() => ({
    handleFormSubmit,
    processFormEmails
  }), [handleFormSubmit, processFormEmails]);
};